import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import {
  FileUpload,
  FileUploadProps,
  PowerProfileMode,
  ToggleButtonGroup,
  powerProfileModes,
} from 'greener-ui-library';
import { fileUploadStyles, modeToggleStyles, powerProfileValidation, sectionTitleStyles } from './utils';

export interface PowerProfileFileUploadProps {
  /** Current selected mode */
  mode: PowerProfileMode;
  /** Callback when mode changes */
  onModeChange: (mode: PowerProfileMode) => void;
  /** Callback when file is uploaded and validated */
  onFileUpload: (file: File) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Validation errors from backend */
  validationErrors?: string[];
  /** Whether file is being processed */
  isProcessing?: boolean;
  /** Whether file was successfully processed */
  isSuccess?: boolean;
}

/**
 * Specialized file upload component for Power Profile data
 * Combines mode/unit toggles with file upload functionality
 */
export const PowerProfileFileUpload: React.FC<PowerProfileFileUploadProps> = ({
  mode,
  onModeChange,
  onFileUpload,
  disabled = false,
  validationErrors = [],
  isProcessing = false,
  isSuccess = false,
}) => {
  const { t } = useTranslation();
  const [, setSelectedFile] = useState<File | null>(null);

  const handleFileUpload = useCallback(
    (file: File) => {
      onFileUpload(file);
    },
    [onFileUpload]
  );

  const handleModeChange = useCallback(
    // ToggleButtonGroup has type conflict between our interface and MUI
    (value: unknown) => {
      if (value && typeof value === 'string') {
        onModeChange(value as PowerProfileMode);
      }
    },
    [onModeChange]
  );

  const successMessage = t('blueprints:wizard.fields.powerProfileGeneration.instructions.messages.success');

  const fileUploadLabels: FileUploadProps['labels'] = {
    dropzone: t('blueprints:wizard.fields.powerProfileGeneration.instructions.description'),
    fileTypes: t('blueprints:wizard.fields.powerProfileGeneration.instructions.fileTypes'),
    fileSize: t('blueprints:wizard.fields.powerProfileGeneration.instructions.maxSize'),
    success: successMessage,
    errorFileSize: t('blueprints:wizard.fields.powerProfileGeneration.instructions.messages.errorFileSize'),
    errorFileType: t('blueprints:wizard.fields.powerProfileGeneration.instructions.messages.errorFileType'),
  };

  const fileUploadInstructions = [
    t('blueprints:wizard.fields.powerProfileGeneration.instructions.requirements.missingValues'),
    t('blueprints:wizard.fields.powerProfileGeneration.instructions.requirements.processing'),
  ];

  return (
    <Box>
      <Stack spacing={3}>
        {/* Mode Toggle */}
        <Box sx={modeToggleStyles}>
          <ToggleButtonGroup
            selectedValue={mode}
            onChange={handleModeChange}
            exclusive={true}
            disabled={disabled}
            buttons={[
              {
                value: powerProfileModes.manual,
                content: t('blueprints:wizard.fields.powerProfileGeneration.modeToggle.manual'),
                buttonProps: {
                  variant: 'primary' as const,
                },
              },
              {
                value: powerProfileModes.file,
                content: t('blueprints:wizard.fields.powerProfileGeneration.modeToggle.file'),
                buttonProps: {
                  variant: 'primary' as const,
                },
              },
            ]}
            spacing={0}
            fullWidth
          />
        </Box>

        {/* File Upload - only show when File mode is selected */}
        {mode === powerProfileModes.file && (
          <Box sx={fileUploadStyles}>
            <Typography variant="h4" sx={sectionTitleStyles}>
              {t('blueprints:wizard.fields.powerProfileGeneration.uploadDataFile')}
            </Typography>

            <FileUpload
              validation={powerProfileValidation}
              labels={fileUploadLabels}
              instructions={fileUploadInstructions}
              error={validationErrors.length > 0 ? validationErrors[0] : undefined}
              success={isSuccess && validationErrors.length === 0}
              onFileSelect={(file) => {
                setSelectedFile(file);
                // Call handleFileUpload immediately when file is selected
                handleFileUpload(file);
              }}
              onValidationResult={(result) => {
                return result;
                // This is called for validation errors, but we handle file upload in onFileSelect
                // No need to do anything here since onFileSelect handles successful uploads
              }}
              readOnly={disabled || isProcessing}
              showInstructions={false}
              loading={isProcessing}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};
